/**
 * Live stock: reading a company's own stock sheet, and the one rule that
 * matters about it — **an aggregate is as much of a disclosure as a row**, so
 * price and value are redacted before this module's output ever reaches
 * somebody without `stock.view.prices`. `redactStockPrices()` is the one
 * function that decision runs through; the server calls it, never the screen,
 * for the same reason `export_customers()` is `security invoker` rather than
 * trusting a hidden column in the browser.
 *
 * Unlike receivables, a stock row has no identity to reconcile against an
 * existing customer and nothing is written anywhere — every read is the
 * current sheet, parsed and handed back. There is no sync, no baseline, no
 * plan; `readStockTable()` is the whole pipeline.
 */
import { isSheetBlank } from './import.js';
import { matchesSearch } from './search.js';
import { STOCK_PRICE_FIELDS, type StockColumnMap, type StockField } from './stockMapping.js';

export interface StockItem {
    /** The row's own identity: its code if the sheet gave one, else its name. */
    id: string;
    code: string | null;
    name: string;
    category: string | null;
    unit: string | null;
    quantity: number | null;
    /** Redacted to `null` server-side for a caller without `stock.view.prices`. */
    rate: number | null;
    /** Redacted alongside `rate`, for the same reason. */
    value: number | null;
    minLevel: number | null;
    maxLevel: number | null;
    lastReceivedOn: string | null;
    status: string | null;
    note: string | null;
}

export type StockAvailability = 'in' | 'low' | 'out';

/**
 * Where an item sits against its own levels, not a company-wide guess.
 *
 * Absent a minimum, "low" cannot be answered — an item with no stated floor is
 * either `in` or `out` and nothing between, rather than defaulting to a
 * threshold nobody chose.
 */
export function stockAvailability(item: Pick<StockItem, 'quantity' | 'minLevel'>): StockAvailability {
    const qty = item.quantity ?? 0;
    if (qty <= 0) return 'out';
    if (item.minLevel != null && qty <= item.minLevel) return 'low';
    return 'in';
}

const parseStockNumber = (raw: string | undefined): number | null => {
    // Deliberately not `isSheetBlank()`: that treats a written "0" as the
    // absence of a value, which is right for a receivables balance ("0" often
    // means "nothing owed, entered by hand") and wrong here — a quantity of
    // zero is the single most important number a stock sheet can hold, an
    // item genuinely out of stock, and must not be read the same as a blank
    // cell that says nothing about it at all.
    if (raw === undefined) return null;
    const v = raw.trim().toUpperCase();
    if (!v || v.startsWith('#') || v === 'NA' || v === 'N/A' || v === 'NIL' || v === 'NULL' || v === 'NONE') return null;
    const cleaned = raw.replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
};

const cell = (row: string[], headers: string[], column: string | undefined): string | undefined => {
    if (!column) return undefined;
    const at = headers.indexOf(column);
    if (at < 0) return undefined;
    const raw = row[at];
    return raw === undefined ? undefined : raw.trim();
};

const textOrNull = (raw: string | undefined): string | null =>
    raw === undefined || isSheetBlank(raw) ? null : raw;

/**
 * The whole sheet, read under a confirmed column map.
 *
 * Rows with no name at all are skipped — a blank line at the end of a sheet is
 * common and is not an item — everything else is kept, including a row with a
 * zero or missing quantity, so "we have none of this" stays visible rather
 * than disappearing from the list.
 */
export function readStockTable(table: string[][], columns: StockColumnMap): StockItem[] {
    if (table.length === 0) return [];
    const headers = (table[0] ?? []).map(h => (h ?? '').trim());
    const items: StockItem[] = [];

    for (let r = 1; r < table.length; r++) {
        const row = table[r] ?? [];
        const name = cell(row, headers, columns.name);
        if (!name || isSheetBlank(name)) continue;

        const code = textOrNull(cell(row, headers, columns.code));
        items.push({
            id: code ?? `${name}#${r}`,
            code,
            name,
            category: textOrNull(cell(row, headers, columns.category)),
            unit: textOrNull(cell(row, headers, columns.unit)),
            quantity: parseStockNumber(cell(row, headers, columns.quantity)),
            rate: parseStockNumber(cell(row, headers, columns.rate)),
            value: parseStockNumber(cell(row, headers, columns.value)),
            minLevel: parseStockNumber(cell(row, headers, columns.minLevel)),
            maxLevel: parseStockNumber(cell(row, headers, columns.maxLevel)),
            lastReceivedOn: textOrNull(cell(row, headers, columns.lastReceivedOn)),
            status: textOrNull(cell(row, headers, columns.status)),
            note: textOrNull(cell(row, headers, columns.note)),
        });
    }

    return items;
}

/**
 * The redaction. Blanks `rate` and `value` to `null` and nothing else —
 * quantity, level, status and every other field are the same for every role,
 * exactly as the reference app never gated availability, only money.
 */
export function redactStockPrices(items: readonly StockItem[]): StockItem[] {
    return items.map(item => ({ ...item, rate: null, value: null }));
}

/** Which sheet columns are money, for a caller that only has field names. */
export const stockPriceColumns = (columns: StockColumnMap): string[] =>
    STOCK_PRICE_FIELDS.map(f => columns[f as StockField]).filter((h): h is string => Boolean(h));

export interface StockFilters {
    search: string;
    category: string;
    availability: 'all' | StockAvailability;
}

export const EMPTY_STOCK_FILTERS: StockFilters = { search: '', category: 'all', availability: 'all' };

export function matchesStockFilters(item: StockItem, filters: StockFilters): boolean {
    if (filters.availability !== 'all' && stockAvailability(item) !== filters.availability) return false;
    if (filters.category !== 'all' && (item.category ?? '') !== filters.category) return false;
    return matchesSearch([item.name, item.code, item.category], filters.search);
}

export interface StockSummary {
    total: number;
    inStock: number;
    low: number;
    out: number;
    /** `null` when nobody in this read had `stock.view.prices` — not zero. */
    totalValue: number | null;
    categories: string[];
}

export function summariseStock(items: readonly StockItem[]): StockSummary {
    let inStock = 0, low = 0, out = 0;
    let value = 0, anyPriced = false;
    const categories = new Set<string>();

    for (const item of items) {
        const availability = stockAvailability(item);
        if (availability === 'in') inStock++;
        else if (availability === 'low') low++;
        else out++;

        if (item.value != null) { value += item.value; anyPriced = true; }
        if (item.category) categories.add(item.category);
    }

    return {
        total: items.length, inStock, low, out,
        totalValue: anyPriced ? value : null,
        categories: Array.from(categories).sort((a, b) => a.localeCompare(b)),
    };
}
